import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CMSProvider } from './context/CMSContext';
import { useToast } from './hooks/useToast';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Toast from './components/shared/Toast';
import FeatureGate from './components/shared/FeatureGate';  // CAP-008
import { ascoltaTastoIndietro } from './utils/appNativa';
import Login from './pages/Login';
import Home from './pages/Home';
import Hub from './pages/Hub';
import Cantieri from './pages/Cantieri';
import Clienti from './pages/Clienti';
import Fatture from './pages/Fatture';
import Preventivi from './pages/Preventivi';
import Listino from './pages/Listino';
import Fornitori from './pages/Fornitori';
import Documenti from './pages/documenti/Documenti';
import Impostazioni from './pages/Impostazioni';
import Calendario from './pages/Calendario';
import AIMonitor from './pages/AIMonitor';
import Governance from './pages/Governance';
import Novita from './pages/Novita';
import OAuthCallback from './pages/OAuthCallback';
import Registrati from './pages/Registrati';
import RegistratiEmail from './pages/RegistratiEmail';
import VerificaEmail from './pages/VerificaEmail';
import PasswordDimenticata from './pages/PasswordDimenticata';
import ResetPassword from './pages/ResetPassword';
import VerifyMfa from './pages/VerifyMfa';
import Supporto from './pages/Supporto';
import Guida from './pages/Guida';
import Assistente from './pages/Assistente';
import InvitaEGuadagna from './pages/InvitaEGuadagna';
import Integrazioni from './pages/Integrazioni';
import Condivisione from './pages/Condivisione';
import AccesoEsterno from './pages/AccesoEsterno';
// CAP-044 (DEC-287): pagina di prova del registratore, fuori dalla cornice e scaricata solo quando si apre.
const ProvaRegistratore = lazy(() => import('./pages/ProvaRegistratore'));

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(var(--bg))' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="gradient-brand" style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
          <span style={{ color: 'white', fontWeight: 700, fontSize: 18, fontStyle: 'italic' }}>m</span>
        </div>
        <div style={{ fontSize: 12, color: 'rgb(var(--muted))' }}>Caricamento…</div>
      </div>
    </div>
  );
  return user ? children : <Navigate to="/auth/login" />;
}

function AppRoutes() {
  const { toasts, showToast, dismissToast } = useToast();

  useEffect(() => {
    const handler = (e) => showToast(e.detail.message, 'error');
    window.addEventListener('api-error', handler);
    return () => window.removeEventListener('api-error', handler);
  }, [showToast]);

  // FE-110: tasto o gesto Indietro di Android dentro l'app (nel browser non fa niente).
  useEffect(() => ascoltaTastoIndietro(), []);

  return (
    <>
    <Routes>
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/auth/registrati" element={<Registrati />} />
      <Route path="/auth/registrati-email" element={<RegistratiEmail />} />
      <Route path="/auth/verifica-email" element={<VerificaEmail />} />
      <Route path="/auth/password-dimenticata" element={<PasswordDimenticata />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify-mfa" element={<VerifyMfa />} />
      <Route path="/condivisione/:token" element={<Condivisione />} />
      <Route path="/accesso-esterno" element={<AccesoEsterno />} />
      <Route path="/prova/registratore" element={<PrivateRoute><Suspense fallback={<div style={{ padding: 24, fontSize: 12, color: 'rgb(var(--muted))' }}>Caricamento…</div>}><ProvaRegistratore /></Suspense></PrivateRoute>} />
      <Route path="/*" element={
        <PrivateRoute>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              {/* Hub delle macro-voci (viste presentazionali → route esistenti) */}
              <Route path="/operativita" element={<Hub macroKey="operativita" />} />
              <Route path="/clienti-vendite" element={<Hub macroKey="clienti-vendite" />} />
              <Route path="/acquisti" element={<Hub macroKey="acquisti" />} />
              <Route path="/maistro-ai" element={<Hub macroKey="maistro-ai" />} />
              <Route path="/servizi" element={<Hub macroKey="servizi" />} />
              <Route path="/assistente" element={<Assistente />} />
              <Route path="/invita" element={<InvitaEGuadagna />} />
              <Route path="/governance" element={<Governance />} />
              <Route path="/cantieri" element={<Cantieri />} />
              <Route path="/cantieri/nuovo" element={<Cantieri />} />
              <Route path="/cantieri/:id" element={<Cantieri />} />
              <Route path="/clienti" element={<Clienti />} />
              <Route path="/clienti/nuovo" element={<Clienti />} />
              <Route path="/fornitori" element={<Fornitori />} />
              <Route path="/fatture" element={<Fatture />} />
              <Route path="/fatture/nuovo" element={<Fatture />} />
              <Route path="/preventivi" element={<Preventivi />} />
              <Route path="/preventivi/nuovo" element={<Preventivi />} />
              <Route path="/listino" element={<Listino />} />
              <Route path="/documenti" element={<Documenti />} />
              <Route path="/documenti/nuovo" element={<Documenti />} />
              <Route path="/calendario" element={<Calendario />} />
              <Route path="/calendario/nuovo" element={<Calendario />} />
              <Route path="/ai-monitor" element={<AIMonitor />} />
              <Route path="/novita" element={<FeatureGate flag="novita_settore" upsellText="Novità del settore: disponibile in un piano superiore"><Novita /></FeatureGate>} />
              <Route path="/supporto" element={<FeatureGate flag="supporto_ticket" upsellText="Supporto ticket: disponibile in un piano superiore"><Supporto /></FeatureGate>} />
              <Route path="/guida" element={<Guida />} />
              <Route path="/integrazioni" element={<Integrazioni />} />
              <Route path="/impostazioni" element={<Impostazioni />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Layout>
        </PrivateRoute>
      } />
    </Routes>
    <Toast toasts={toasts} dismissToast={dismissToast} />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <CMSProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </CMSProvider>
    </ErrorBoundary>
  );
}
